import { useEffect, useRef, useState } from 'react'
import { useDeleteMedia, useUpdateMedia } from '@/hooks/queries'
import type { Media } from '@/models/media'
import { TagEditor } from './ui/TagEditor'

interface MediaEditorProps {
  media: Media
  onClose: () => void
  onDeleted: () => void
}

/**
 * Edit one image in the library: rename it, change its tags, or delete it.
 * Rendered on /my/media when an image is selected. Remount it per image
 * (key={media.id}) so the form starts from that image's values.
 */
export function MediaEditor({ media, onClose, onDeleted }: MediaEditorProps) {
  const [name, setName] = useState(media.name ?? '')
  const [tags, setTags] = useState<string[]>(media.tags ?? [])
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const update = useUpdateMedia()
  const remove = useDeleteMedia()

  // The editor opens below the grid; bring it into view on a long library.
  const panel = useRef<HTMLElement>(null)
  useEffect(() => {
    panel.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [])

  const busy = update.isPending || remove.isPending
  const trimmed = name.trim()
  const changed =
    trimmed !== (media.name ?? '') || JSON.stringify(tags) !== JSON.stringify(media.tags ?? [])

  const save = () => {
    if (media.id === null || !trimmed || !changed) return
    update.mutate({ id: media.id, name: trimmed, tags })
  }

  const confirmDelete = () => {
    if (media.id === null) return
    remove.mutate(media.id, {
      onSuccess: onDeleted,
      onSettled: () => setConfirmingDelete(false),
    })
  }

  return (
    <section className="media-editor" aria-label="Edit image" ref={panel}>
      <div className="media-editor-preview">
        {media.src ? <img src={media.src} alt={trimmed || 'Selected image'} /> : null}
      </div>

      <form
        className="media-editor-form"
        onSubmit={(event) => {
          event.preventDefault()
          save()
        }}
      >
        <label className="media-editor-label" htmlFor="media-editor-name">
          Name
        </label>
        <input
          id="media-editor-name"
          className="media-editor-input"
          value={name}
          maxLength={100}
          onChange={(event) => setName(event.target.value)}
          aria-invalid={trimmed === '' || undefined}
        />
        {trimmed === '' ? <div className="field-error">Give the image a name.</div> : null}

        <div className="media-editor-label">Tags</div>
        <TagEditor tags={tags} onChange={setTags} label="tag" className="media-editor-tags" />

        {media.created_at ? (
          <div className="media-editor-meta">
            Uploaded {media.created_at.toLocaleDateString(undefined, { dateStyle: 'medium' })}
          </div>
        ) : null}

        <div className="media-editor-actions">
          {confirmingDelete ? (
            <>
              <span className="media-actions-prompt">Delete this image?</span>
              <button
                type="button"
                className="media-action"
                onClick={() => setConfirmingDelete(false)}
                disabled={busy}
              >
                Keep
              </button>
              <button
                type="button"
                className="media-action danger"
                onClick={confirmDelete}
                disabled={busy}
              >
                {remove.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="media-action danger"
                onClick={() => setConfirmingDelete(true)}
                disabled={busy}
              >
                Delete
              </button>
              <span className="media-editor-spacer" />
              <button type="button" className="media-action" onClick={onClose} disabled={busy}>
                Close
              </button>
              <button
                type="submit"
                className="media-action primary"
                disabled={busy || !changed || trimmed === ''}
              >
                {update.isPending ? 'Saving…' : 'Save'}
              </button>
            </>
          )}
        </div>
      </form>
    </section>
  )
}
